import React from 'react';
import { Radio } from '@mui/material';
import type { RadioProps } from '@mui/material';

export interface LookupRadioProps extends Omit<RadioProps, 'size'> {
  /** Whether the radio button is checked */
  checked: boolean;
  /** Value of the radio button */
  value: string | number;
  /** Size of the radio button - defaults to 'small' for lookup lists */
  size?: 'small' | 'medium';
  /** Custom styling */
  sx?: RadioProps['sx'];
}

/**
 * LookupRadio - Reusable Radio Button for Lookup Lists
 *
 * A standardized radio button component for lookup/selection modals that provides:
 * - Consistent styling optimized for lookup lists
 * - Small size by default for compact display
 * - Standard Material-UI Radio functionality
 * - Customizable appearance and behavior
 *
 * @example
 * ```tsx
 * <LookupRadio
 *   checked={selectedId === item.id}
 *   value={item.id}
 *   onChange={handleRadioChange}
 * />
 * ```
 */
export const LookupRadio: React.FC<LookupRadioProps> = ({
  checked,
  value,
  size = 'small',
  sx,
  ...radioProps
}) => {
  return (
    <Radio
      size={size}
      checked={checked}
      value={value}
      sx={{
        mr: 1,
        ...sx
      }}
      {...radioProps}
    />
  );
};

export default LookupRadio;